<template>
  <div class="tooltip">
    <div ref="target" class="tooltip__target" :style="{ 'max-width': truncateWidth }">
      <div class="tooltip__target-content" :class="{ 'tooltip__target-content--truncate': truncateWidth }">
        <slot />
      </div>
    </div>
    <UIBaseTransition>
      <div
        v-if="hasSlotContent(slots.content) && !disabled && (truncateWidth ? isTextLongerThanTruncateWidth : true)"
        ref="content"
        :style="{
          ...floatingStyles,
          maxWidth: contentWidth,
        }"
        class="tooltip__content"
      >
        <div
          ref="floatingArrow"
          :style="{
            position: 'absolute',
            left: `${floatingArrowStyles.x}px`,
            [staticSide]: `-${arrowSizeHalf}px`,
          }"
          class="tooltip__arrow"
        ></div>
        <slot name="content" />
      </div>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, useFloating, arrow, offset, type Placement, type Strategy } from '@floating-ui/vue'
import { hasSlotContent } from '../../helpers/slot.helper'

const arrowSize = 6
const arrowSizePx = `${arrowSize}px`
const arrowSizeHalf = arrowSize / 2

const slots = useSlots()

const target = ref<HTMLDivElement | null>(null)
const content = ref<HTMLDivElement | null>(null)
const floatingArrow = ref<HTMLDivElement | null>(null)

const props = withDefaults(
  defineProps<{
    hover?: boolean
    placement?: Placement
    strategy?: Strategy
    fallbackPlacements?: Placement[]
    contentWidth?: CSSStyleDeclaration['width']
    disabled?: boolean
    truncateWidth?: CSSStyleDeclaration['maxWidth']
  }>(),
  {
    placement: 'bottom',
    strategy: 'absolute',
    fallbackPlacements: () => ['top', 'bottom'],
    contentWidth: 'auto',
    hover: true,
    disabled: false,
    truncateWidth: undefined,
  }
)

const isTextLongerThanTruncateWidth = ref(false)

const getIsTextLongerThanTruncateWidth = () => {
  if (!props.truncateWidth || !target.value) {
    return false
  }

  const [children] = target.value?.children || []

  return Boolean(props.truncateWidth) && children.scrollWidth > Number(props.truncateWidth.replace('px', ''))
}

const isHovered = useElementHover(target)

const { floatingStyles, middlewareData, placement } = useFloating(target, content, {
  placement: props.placement,
  whileElementsMounted: autoUpdate,
  strategy: props.strategy,
  middleware: [
    flip({
      fallbackPlacements: props.fallbackPlacements,
    }),
    arrow({ element: floatingArrow, padding: 10 }),
    offset(4.3),
  ],
  open: props.hover ? isHovered.value && hasSlotContent(slots.content) : hasSlotContent(slots.content),
})

const staticSide: any = computed(
  () =>
    ({
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.value.split('-')[0]])
)

const floatingArrowStyles = computed(() => middlewareData.value.arrow || { x: '', y: '' })

const { stop } = useIntersectionObserver(target, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    nextTick(() => {
      isTextLongerThanTruncateWidth.value = getIsTextLongerThanTruncateWidth()

      stop()
    })
  }
})
</script>

<style scoped lang="scss">
@use '@/styles/mixins.scss' as *;
@use '@/styles/variables.scss' as *;

.tooltip {
  &__target {
    display: flex;
    flex-direction: column;

    &-content {
      display: flex;
      flex-direction: column;

      &--truncate {
        display: block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &:hover {
      & + .tooltip__content {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  &__content {
    background-color: $black-100;
    position: absolute;
    padding: 6px 8px;
    z-index: 4;
    color: white;
    border-radius: 4px;
    box-shadow: $base-box-shadow;
    opacity: 0;
    visibility: hidden;
    white-space: normal;
    text-align: left;
    transition: $base-transition opacity, $base-transition visibility;
    @include body3();

    @media screen and (max-width: 1023px) {
      font-size: 10px;
    }
  }

  &__arrow {
    position: absolute;
    width: v-bind(arrowSizePx);
    height: v-bind(arrowSizePx);
    background: white;
    z-index: 1;
    pointer-events: none;
    transform: rotate(45deg);
    background-color: $black-100;
  }
}
</style>
